import useApiRequest from "@/lib/hooks/use-request";
import { DealKanbanResponseDto, DealKanbanStageListResponseDto } from "@/lib/interfaces/deals";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

const KanbanBoard = ({ items }: { items: DealKanbanStageListResponseDto[] }) => {
  const { apiRequest } = useApiRequest();
  const [stages, setStages] = useState(items);

  const updateStage = async (stageId: string, dealId: string) => {
    const success = onStageChange(stageId, dealId);
    if (!success) return;
    await apiRequest(`deals/${dealId}/stage`, "PATCH", {
      body: {
        belongsToStageId: stageId,
      },
    });
  };

  const onStageChange = (stageId: string, dealId: string) => {
    const newStages = [...stages];

    // get the deal
    const deal = newStages.flatMap((stage) => stage.deals).find((deal) => deal._id === dealId);
    if (!deal) return false;

    // get the previous stage
    const previousStage = newStages.find((stage) => stage.deals.some((deal) => deal._id === dealId));
    if (previousStage) {
      previousStage.deals = previousStage.deals.filter((deal) => deal._id !== dealId);
    }

    if (previousStage?._id === stageId) return false;

    // get the new stage
    const newStage = newStages.find((stage) => stage._id === stageId);
    if (newStage) {
      newStage.deals.push(deal);
    }
    setStages(newStages);
    return true;
  };

  const handleDrop = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData("dealId");
    updateStage(stageId, dealId);
  };

  return (
    <div className="flex gap-4 overflow-x-auto min-h-[calc(100vh-200px)]">
      {items.map((stage) => {
        return (
          <div
            key={stage._id}
            className="flex flex-col min-w-[300px] bg-slate-50 rounded-lg shadow kanban-column transition-colors"
          >
            <div className="p-3 font-medium text-slate-700 border-b bg-slate-100 rounded-t-lg">
              {stage.title}
              <span className="ml-2 text-sm text-slate-500">({stage.deals.length})</span>
            </div>
            <div 
              className="p-2 flex flex-col gap-2 h-full"
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('drag-over');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
                handleDrop(e, stage._id);
              }}
            >
              {stage.deals.map((deal) => (
                <KanbanItem deal={deal} key={deal._id} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const KanbanItem = ({ deal }: { deal: DealKanbanResponseDto }) => {
  const navigate = useNavigate();
  const formattedDate = new Date(deal.createdAt).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div
      key={deal._id}
      draggable={deal.isEditable}
      onDragStart={(e) => {
        if (!deal.isEditable) return;
        e.dataTransfer.setData("dealId", deal._id);
        // Add ghost image effect
        const draggedElement = e.target as HTMLElement;
        const clone = draggedElement.cloneNode(true) as HTMLElement;
        clone.style.width = `${draggedElement.offsetWidth}px`;
        clone.style.backgroundColor = 'white';
        clone.style.opacity = '0.8';
        clone.style.position = 'absolute';
        clone.style.top = '-1000px';
        document.body.appendChild(clone);
        e.dataTransfer.setDragImage(clone, 0, 0);
        // Add dragging class to original element
        draggedElement.classList.add('dragging');
        setTimeout(() => document.body.removeChild(clone), 0);
      }}
      onDragEnd={(e) => {
        if (!deal.isEditable) return;
        e.preventDefault();
        const elements = document.querySelectorAll('.kanban-column');
        elements.forEach(el => el.classList.remove('drag-over'));
        (e.target as HTMLElement).classList.remove('dragging');
      }}
      onClick={() => navigate(`/deals/${deal._id}`)}
      className={`bg-white p-3 rounded shadow-sm transition-all select-none ${
        deal.isEditable 
          ? 'cursor-move border-2 border-slate-200 hover:border-blue-400 hover:shadow-md hover:shadow-blue-100' 
          : 'cursor-not-allowed opacity-60 border border-slate-300 bg-slate-50'
      }`}
    >
      <Tippy content={deal.title}>
        <div className="font-medium text-slate-800 truncate max-w-[250px]">
          {deal.title}
        </div>
      </Tippy>
      
      <div className="mt-2 space-y-1">
        {/* Person */}
        <div className="flex items-center text-sm">
          <svg className="w-4 h-4 mr-1 text-slate-400 flex-shrink-0" fill="none" strokeWidth="1.5" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
          <div className="min-w-0 flex-1">
            {deal.summary.personSummary ? (
              <Tippy content={deal.summary.personSummary.title}>
                <Link 
                  to={`/contacts/persons/${deal.summary.personSummary._id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-blue-600 hover:underline truncate block max-w-[200px]"
                >
                  {deal.summary.personSummary.title}
                </Link>
              </Tippy>
            ) : (
              <span className="text-slate-400">Keine Person</span>
            )}
          </div>
        </div>

        {/* Organization */}
        <div className="flex items-center text-sm">
          <svg className="w-4 h-4 mr-1 text-slate-400 flex-shrink-0" fill="none" strokeWidth="1.5" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
          </svg>
          <div className="min-w-0 flex-1">
            {deal.summary.organizationSummary ? (
              <Tippy content={deal.summary.organizationSummary.title}>
                <Link 
                  to={`/contacts/organisations/${deal.summary.organizationSummary._id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-blue-600 hover:underline truncate block max-w-[200px]"
                >
                  {deal.summary.organizationSummary.title}
                </Link>
              </Tippy>
            ) : (
              <span className="text-slate-400">Kein Unternehmen</span>
            )}
          </div>
        </div>

        {/* Owner and Date */}
        <div className="flex items-center justify-between text-sm text-slate-500 pt-1">
          <Tippy content={deal.belongsToUserFullName}>
            <div className="truncate max-w-[120px]">
              {deal.belongsToUserFullName}
            </div>
          </Tippy>
          <div className="flex-shrink-0">{formattedDate}</div>
        </div>
      </div>
    </div>
  );
};

export default KanbanBoard;
